// RunningIndicator — the app shell's "a test is running" pill (both chromes).
//
// A scanner book can run for 20+ minutes, most of it waiting on a busy
// market-data lake, and the page that started it held the job id in component
// state only: after a reload nothing said it was still going. The api now
// lists every queued / running test of the user (GET /api/backtest/active
// `jobs`, crypto-backend-api backtest/activeJobs.js); this reads that list
// through the shared cache (qk.backtestRunning) and is its one polling
// observer — every 5 s while something runs, every 60 s otherwise, paused
// while the tab is hidden.
//
// Nothing runs → nothing drawn. One test → "Testing · <label> · 42%" (or
// "Waiting for data…"). Several → "N tests running". The pill opens a list:
// label, progress or the wait line, Open, Stop. When a test leaves the list
// its status is read once and a finished one toasts "Test finished — Open".
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import backtestService from '../../services/backtestService';
import { useRunningJobsQuery, qk } from '../data/queries';
import { useAppQueryClient } from '../data/queryClient';
import Popover from '../components/Popover';
import Button from '../components/Button';
import { toast } from '../components/Toast';
import { cx } from '../components/util';
import { stageLabel } from '../../components/Backtest/runningJobs';

const enc = encodeURIComponent;

const KIND_WORD = {
  book: 'Scanner test', draft: 'Rule test', backtest: 'Backtest', sweep: 'Sweep', walkforward: 'Walk-forward', replay: 'Replay',
};

/** The job's name: its label, else what kind of test it is. */
export const jobName = (job) => (job && job.label) || KIND_WORD[job && job.kind] || 'Test';

/**
 * Where the job's result appears (and where the page re-attaches to it):
 *   book, saved scanner  its sheet with the book drawer open (BookTest);
 *   book, draft only     the scanner wizard on that draft, at step 2, where its
 *                        BookTest shows the draft's run;
 *   draft                the bot wizard on that draft (its test block);
 *   sweep                Backtests → Optimize on the bot, adopting the sweep;
 *   walk-forward         Backtests → the bot's walk-forward view, adopting it;
 *   backtest             Backtests on the bot (it adopts GET /active's run).
 */
export function jobHref(job) {
  const j = job || {};
  if (j.kind === 'book') {
    if (j.scannerId) return `/profile/scanners?scanner=${enc(j.scannerId)}&book=1`;
    if (j.draftId) return `/profile/scanners/new?draft=${enc(j.draftId)}&step=1`;
    return '/profile/scanners';
  }
  if (j.kind === 'draft') return j.draftId ? `/profile/traderform?draft=${enc(j.draftId)}` : '/profile/traderform?tab=drafts';
  if ((j.kind === 'sweep' || j.kind === 'walkforward') && j.botId) {
    return `/profile/traderform?tab=backtest&bot=${enc(j.botId)}&do=${j.kind === 'sweep' ? 'optimize' : 'robust'}&job=${enc(j.jobId || '')}`;
  }
  if (j.botId) return `/profile/traderform?tab=backtest&bot=${enc(j.botId)}`;
  return '/profile/traderform?tab=backtest';
}

const pctOf = (job) => {
  const p = Number(job && job.progress);
  return Number.isFinite(p) && p > 0 ? `${Math.min(100, Math.round(p))}%` : null;
};

/** One short state: "Waiting for data…", "Preparing the book…", "42%", or "Queued". */
export function jobState(job) {
  if (job && job.waiting) return 'Waiting for data…';
  const p = pctOf(job);
  // A light-lane attempt failed and BullMQ will run it again (api worker).
  if (String((job && job.status) || '') === 'retrying') return 'Retrying';
  // A scanner book's preparation step (api `stage`); 'simulating' shows the percentage.
  const stage = stageLabel(job && job.stage);
  if (stage) return stage;
  if (p) return p;
  return /queued|waiting|prioritized|delayed/.test(String((job && job.status) || '')) ? 'Queued' : 'Starting';
}

/** The pill's text. */
export function pillText(jobs, { phone = false } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];
  if (!list.length) return '';
  if (list.length > 1) return phone ? `${list.length} running` : `${list.length} tests running`;
  const j = list[0];
  if (phone) return j.waiting ? 'Waiting…' : `Testing${pctOf(j) ? ` ${pctOf(j)}` : ''}`;
  if (j.waiting) return `Waiting for data… · ${jobName(j)}`;
  // One scanner book preparing (api `stage`): the step in place of "Testing".
  const stage = stageLabel(j.stage);
  return stage ? `${stage} · ${jobName(j)}` : ['Testing', jobName(j), pctOf(j)].filter(Boolean).join(' · ');
}

/** Toast the jobs that left the list, once each, by their final status. */
function useFinishToasts(jobs, loaded, history, stoppedHere) {
  const prev = useRef(null);
  useEffect(() => {
    if (!loaded) return;
    const now = new Map((jobs || []).map((j) => [j.jobId, j]));
    const before = prev.current;
    prev.current = now;
    if (!before) return;
    before.forEach((j, id) => {
      if (now.has(id) || stoppedHere.current.has(id)) return;
      backtestService.getStatus(id).then((s) => {
        const st = String((s && s.status) || '');
        const open = { label: 'Open', onClick: () => history.push(jobHref(j)) };
        if (st === 'completed') toast.show({ id: `bt-done-${id}`, tone: 'ok', title: 'Test finished', body: jobName(j), action: open });
        else if (st === 'failed') toast.show({ id: `bt-done-${id}`, tone: 'warn', title: 'Test failed', body: (s && s.reason) || jobName(j), action: open });
      }).catch(() => {});
    });
  }, [jobs, loaded, history, stoppedHere]);
}

function JobRow({ job, onOpen, onStop, stopping }) {
  const p = Number(job.progress);
  return (
    <div className="ui-running__row" data-testid="running-job">
      <div className="ui-running__name" title={jobName(job)}>{jobName(job)}</div>
      <div className="ui-running__state">
        {job.waiting ? <span className="ui-watch-text">{job.message || 'Waiting for data…'}</span> : <span>{jobState(job)}</span>}
      </div>
      {!job.waiting && !stageLabel(job.stage) && Number.isFinite(p) && p > 0 ? (
        <div className="ui-running__bar" aria-hidden="true"><span style={{ width: `${Math.min(100, p)}%` }} /></div>
      ) : null}
      <div className="ui-running__acts">
        <Button size="sm" onClick={onOpen}>Open</Button>
        <Button size="sm" variant="quiet" onClick={onStop} busy={stopping} busyLabel="Stopping…">Stop</Button>
      </div>
    </div>
  );
}

/**
 * props: enabled (false in tests / outside a session), phone (the short pill).
 */
export default function RunningIndicator({ enabled = true, phone = false }) {
  const q = useRunningJobsQuery({ enabled, poll: true });
  const client = useAppQueryClient();
  const jobs = Array.isArray(q.data) ? q.data : [];
  const history = useHistory();
  const btn = useRef(null);
  const [open, setOpen] = useState(false);
  const [stopping, setStopping] = useState(null);
  const stoppedHere = useRef(new Set());
  useFinishToasts(jobs, q.data !== undefined, history, stoppedHere);

  useEffect(() => { if (!jobs.length) setOpen(false); }, [jobs.length]);

  const go = useCallback((job) => { setOpen(false); history.push(jobHref(job)); }, [history]);
  const stop = useCallback(async (job) => {
    setStopping(job.jobId);
    stoppedHere.current.add(job.jobId);
    try {
      await backtestService.cancel(job.jobId);
    } catch {
      stoppedHere.current.delete(job.jobId);
      toast.warn('The test could not be stopped.');
    } finally {
      setStopping(null);
      client.invalidateQueries({ queryKey: qk.backtestRunning });
    }
  }, [client]);

  if (!enabled || !jobs.length) return null;
  const waiting = jobs.length === 1 && jobs[0].waiting;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={cx('ui-runpill', waiting && 'ui-runpill--wait', phone && 'ui-runpill--phone')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="running-pill"
      >
        <span className="ui-runpill__dot" aria-hidden="true" />
        <span className="ui-runpill__text">{pillText(jobs, { phone })}</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorEl={btn.current} placement="bottom-end" ariaLabel="Running tests" className="ui-running">
        {jobs.map((j) => (
          <JobRow key={j.jobId} job={j} onOpen={() => go(j)} onStop={() => stop(j)} stopping={stopping === j.jobId} />
        ))}
      </Popover>
    </>
  );
}
